/* ============================================================
   MYC PERFORMANCE — product page
   - variant matching -> price, media, availability, hidden id
   - scroll-snap gallery: thumbs / dots / arrows / counter / keys
   - variant colour change -> scrolls gallery to that media
   - full-screen lightbox (zoom button + slide click)
   - size-guide modal
   - pack selector chips -> quantity
   - sticky mobile add-to-cart bar
   Add-to-cart submit itself is handled by myc-theme.js (AJAX drawer);
   this file never posts to the cart — it only drives UI + qty + id.
   ============================================================ */
(function () {
  'use strict';

  function money(cents) {
    var fmt = window.mycMoneyFormat || '£{{amount}}';
    var amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
  }

  function initProduct(root) {
    if (root.dataset.mycPdpInit) return;
    root.dataset.mycPdpInit = '1';

    var dataEl = root.querySelector('[data-myc-product-json]');
    if (!dataEl) return;
    var product;
    try { product = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var form = root.querySelector('form[data-myc-product-form]');
    var idInput = form && form.querySelector('[name="id"]');
    var qtyInput = form && form.querySelector('[name="quantity"]');
    var priceEl = root.querySelector('[data-myc-price]');
    var comparePriceEl = root.querySelector('[data-myc-compare-price]');
    var atcBtn = root.querySelector('[data-myc-atc]');
    var atcLabel = atcBtn ? atcBtn.getAttribute('data-label-add') : 'Add to cart';
    var atcSoldOut = atcBtn ? atcBtn.getAttribute('data-label-soldout') : 'Sold out';
    var atcBackorder = atcBtn ? atcBtn.getAttribute('data-label-backorder') : 'Pre-order';
    var backorderNote = root.querySelector('[data-myc-backorder]');
    var packThreeLabel = atcBtn ? atcBtn.getAttribute('data-label-pack-three') : 'Add custom 3-pack';
    var packFiveLabel = atcBtn ? atcBtn.getAttribute('data-label-pack-five') : 'Add custom 5-pack';
    var colorNameEl = root.querySelector('[data-myc-color-name]');
    var barPriceEl = root.querySelector('[data-myc-bar-price]');
    var barBtn = root.querySelector('[data-myc-bar-atc]');

    var selected = {};
    root.querySelectorAll('[data-myc-option]').forEach(function (btn) {
      if (btn.classList.contains('is-active')) {
        selected[btn.getAttribute('data-option-index')] = btn.getAttribute('data-option-value');
      }
    });

    function currentVariant() {
      return product.variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return selected[String(i)] === opt;
        });
      });
    }

    /* ---------------- gallery carousel ---------------- */
    var gallery = root.querySelector('[data-myc-gallery]');
    var track = root.querySelector('[data-myc-track]');
    var slides = track ? Array.prototype.slice.call(track.querySelectorAll('[data-myc-slide]')) : [];
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-myc-thumb]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-myc-dot]'));
    var counterEl = root.querySelector('[data-myc-counter]');
    var mediaCount = slides.length;
    var activeIndex = 0;

    function slideWidth() {
      return track ? track.clientWidth : 0;
    }

    function setActiveIndex(i, opts) {
      opts = opts || {};
      if (i < 0) i = 0;
      if (i > mediaCount - 1) i = mediaCount - 1;
      activeIndex = i;
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', parseInt(t.getAttribute('data-index'), 10) === i);
      });
      dots.forEach(function (d) {
        d.classList.toggle('is-active', parseInt(d.getAttribute('data-index'), 10) === i);
      });
      if (counterEl) counterEl.textContent = pad2(i + 1);
      if (opts.scroll && track) {
        track.scrollTo({ left: i * slideWidth(), behavior: opts.behavior || 'smooth' });
      }
    }

    // derive active index from the track scroll position (native swipe)
    var scrollRaf = null;
    function onTrackScroll() {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () {
        scrollRaf = null;
        var w = slideWidth();
        if (!w) return;
        var i = Math.round(track.scrollLeft / w);
        if (i !== activeIndex) setActiveIndex(i);
      });
    }
    if (track) track.addEventListener('scroll', onTrackScroll, { passive: true });

    function indexForMediaId(mediaId) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) return i;
      }
      return -1;
    }

    // jump gallery to a variant's featured image (no smooth on first paint)
    function switchMedia(variant, behavior) {
      if (!variant) return;
      // NOTE: this used to short-circuit on a bundled per-colour theme asset, which
      // overwrote slide 0 and never reached the variant's real image from admin.
      if (!variant.featured_media_id) return;
      var i = indexForMediaId(variant.featured_media_id);
      if (i > -1 && i !== activeIndex) setActiveIndex(i, { scroll: true, behavior: behavior || 'smooth' });
    }

    // thumbs / dots / arrows
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        setActiveIndex(parseInt(t.getAttribute('data-index'), 10), { scroll: true });
      });
    });
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        setActiveIndex(parseInt(d.getAttribute('data-index'), 10), { scroll: true });
      });
    });
    var prevBtn = root.querySelector('[data-myc-gallery-prev]');
    var nextBtn = root.querySelector('[data-myc-gallery-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { setActiveIndex(activeIndex - 1, { scroll: true }); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setActiveIndex(activeIndex + 1, { scroll: true }); });

    // keyboard on the focusable track
    if (track) {
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); setActiveIndex(activeIndex - 1, { scroll: true }); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); setActiveIndex(activeIndex + 1, { scroll: true }); }
      });
    }

    // start index from server-rendered markup
    if (track) {
      var startIndex = parseInt(track.getAttribute('data-start-index'), 10) || 0;
      // no animation on load; align silently after layout settles
      setActiveIndex(startIndex);
      if (startIndex > 0) {
        requestAnimationFrame(function () {
          track.scrollTo({ left: startIndex * slideWidth(), behavior: 'auto' });
        });
      }
    }
    // realign on resize (slide width changes)
    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        if (track) track.scrollTo({ left: activeIndex * slideWidth(), behavior: 'auto' });
      });
    });

    /* ---------------- modals (lightbox + size guide) ---------------- */
    var lastFocused = null;
    var modalKeyHandler = null;

    function trapTab(el, e) {
      var f = focusables(el);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !el.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
    }
    function openModal(el, opts) {
      if (!el) return;
      opts = opts || {};
      lastFocused = document.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('myc-modal-open');
      // focus must wait for the element to become visible: it is
      // visibility:hidden until the class applies, and focus() is a no-op on a
      // still-hidden element. rAF covers the normal case; the timeout is a
      // fallback for contexts where rAF is throttled.
      var focusFirst = function () {
        if (el.dataset.mycFocused) return;
        var f = focusables(el);
        if (f.length) { f[0].focus(); el.dataset.mycFocused = '1'; }
      };
      requestAnimationFrame(focusFirst);
      setTimeout(focusFirst, 80);
      // handle keys at document level so Escape works regardless of focus
      modalKeyHandler = function (e) {
        if (e.key === 'Escape') { e.preventDefault(); closeModal(el); }
        else if (e.key === 'Tab') { trapTab(el, e); }
        else if (opts.onKey) opts.onKey(e);
      };
      document.addEventListener('keydown', modalKeyHandler);
    }
    function closeModal(el) {
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      delete el.dataset.mycFocused;
      document.body.classList.remove('myc-modal-open');
      if (modalKeyHandler) { document.removeEventListener('keydown', modalKeyHandler); modalKeyHandler = null; }
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    // ---- lightbox ----
    var lightbox = root.querySelector('[data-myc-lightbox]');
    var lbSlides = lightbox ? Array.prototype.slice.call(lightbox.querySelectorAll('[data-myc-lb-slide]')) : [];
    var lbCounter = lightbox ? lightbox.querySelector('[data-myc-lb-counter]') : null;
    var lbIndex = 0;

    function lbShow(i) {
      if (!lbSlides.length) return;
      if (i < 0) i = lbSlides.length - 1;
      if (i > lbSlides.length - 1) i = 0;
      lbIndex = i;
      lbSlides.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
      if (lbCounter) lbCounter.textContent = pad2(i + 1) + ' / ' + pad2(lbSlides.length);
    }
    function openLightbox(i) {
      lbShow(i || 0);
      openModal(lightbox, {
        onKey: function (e) {
          if (e.key === 'ArrowLeft') lbShow(lbIndex - 1);
          else if (e.key === 'ArrowRight') lbShow(lbIndex + 1);
        }
      });
    }
    if (lightbox) {
      // open from the zoom button (current gallery index)
      var zoomBtn = root.querySelector('[data-myc-zoom]');
      if (zoomBtn) zoomBtn.addEventListener('click', function () { openLightbox(activeIndex); });
      // open by clicking an image slide
      slides.forEach(function (s) {
        if (s.hasAttribute('data-lightbox')) {
          s.addEventListener('click', function () { openLightbox(parseInt(s.getAttribute('data-index'), 10) || 0); });
        }
      });
      lightbox.querySelectorAll('[data-myc-lb-close]').forEach(function (b) {
        b.addEventListener('click', function () { closeModal(lightbox); });
      });
      var lbPrev = lightbox.querySelector('[data-myc-lb-prev]');
      var lbNext = lightbox.querySelector('[data-myc-lb-next]');
      if (lbPrev) lbPrev.addEventListener('click', function () { lbShow(lbIndex - 1); });
      if (lbNext) lbNext.addEventListener('click', function () { lbShow(lbIndex + 1); });
    }

    // ---- size guide ----
    var sizeGuide = root.querySelector('[data-myc-size-guide]');
    if (sizeGuide) {
      root.querySelectorAll('[data-myc-size-guide-open]').forEach(function (b) {
        b.addEventListener('click', function () { openModal(sizeGuide); });
      });
      sizeGuide.querySelectorAll('[data-myc-modal-close]').forEach(function (b) {
        b.addEventListener('click', function () { closeModal(sizeGuide); });
      });
    }

    /* ---------------- pack selector ---------------- */
    var packBtns = Array.prototype.slice.call(root.querySelectorAll('[data-myc-pack]'));
    var bundleBuilder = root.querySelector('[data-myc-bundle-builder]');
    var bundleSlots = bundleBuilder ? Array.prototype.slice.call(bundleBuilder.querySelectorAll('[data-myc-bundle-slot]')) : [];
    var activePackQty = 1;

    var bundleSoldOutLabel = (bundleBuilder && bundleBuilder.getAttribute('data-soldout-label')) || 'Sold out';

    function bundleSlotValues(slot) {
      var values = [];
      slot.querySelectorAll('[data-myc-bundle-option]').forEach(function (select) {
        values[parseInt(select.getAttribute('data-option-index'), 10)] = select.value;
      });
      return values;
    }

    function variantForValues(values) {
      return product.variants.find(function (variant) {
        return variant.options.every(function (option, index) { return values[index] === option; });
      });
    }

    function variantForBundleSlot(slot) {
      return variantForValues(bundleSlotValues(slot));
    }

    /* Is this option value buyable given the slot's OTHER choice? Stock is per
       size+colour combination, not per colour — "Navy" can be in stock in L and
       gone in S — so each value has to be probed against the settled combo. */
    function valueIsAvailable(values, optionIndex, candidate) {
      var probe = values.slice();
      probe[optionIndex] = candidate;
      var variant = variantForValues(probe);
      return !!(variant && variant.available);
    }

    /* Grey out and label every out-of-stock combination in this slot so the
       customer sees it BEFORE choosing. Shopify's multi-item add is atomic —
       a single sold-out line rejects the whole pack with a 422 and adds
       nothing — so this has to be prevented up front, not reported after. */
    function refreshBundleSlotOptions(slot) {
      var selects = Array.prototype.slice.call(slot.querySelectorAll('[data-myc-bundle-option]'));
      if (!selects.length) return;

      // Pass 1 — if a choice just died because the other select moved, fall to
      // the first still-available value so the slot never sits in a dead state.
      selects.forEach(function (select) {
        var optionIndex = parseInt(select.getAttribute('data-option-index'), 10);
        var values = bundleSlotValues(slot);
        if (valueIsAvailable(values, optionIndex, select.value)) return;
        var healed = Array.prototype.filter.call(select.options, function (opt) {
          return valueIsAvailable(values, optionIndex, opt.value);
        })[0];
        if (healed) select.value = healed.value;
      });

      // Pass 2 — label + disable against the settled combination.
      var settled = bundleSlotValues(slot);
      selects.forEach(function (select) {
        var optionIndex = parseInt(select.getAttribute('data-option-index'), 10);
        Array.prototype.forEach.call(select.options, function (opt) {
          if (!opt.dataset.mycLabel) opt.dataset.mycLabel = opt.textContent;
          var ok = valueIsAvailable(settled, optionIndex, opt.value);
          opt.disabled = !ok;
          opt.textContent = ok ? opt.dataset.mycLabel : opt.dataset.mycLabel + ' — ' + bundleSoldOutLabel;
        });
      });

      syncBundleSwatches(slot);
    }

    /* Mirror each colour <select> onto its swatch row. The select stays
       authoritative — this only reflects selection and per-size availability,
       so the swatches can never disagree with what will be added to the cart. */
    function syncBundleSwatches(slot) {
      slot.querySelectorAll('[data-myc-bundle-swatch]').forEach(function (swatch) {
        var optionIndex = swatch.getAttribute('data-option-index');
        var select = slot.querySelector('[data-myc-bundle-option][data-option-index="' + optionIndex + '"]');
        if (!select) return;
        var value = swatch.getAttribute('data-option-value');
        var opt = Array.prototype.filter.call(select.options, function (o) { return o.value === value; })[0];
        var active = select.value === value;
        swatch.classList.toggle('is-active', active);
        swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
        swatch.disabled = !!(opt && opt.disabled);
        if (opt && opt.disabled) swatch.title = value + ' — ' + bundleSoldOutLabel;
        else swatch.title = value;
      });
    }

    function seedBundleSlots() {
      if (!bundleBuilder || bundleBuilder.dataset.seeded) return;
      bundleSlots.forEach(function (slot) {
        slot.querySelectorAll('[data-myc-bundle-option]').forEach(function (select) {
          var optionIndex = select.getAttribute('data-option-index');
          if (selected[optionIndex] !== undefined) select.value = selected[optionIndex];
        });
      });
      bundleBuilder.dataset.seeded = 'true';
    }

    function selectedBundleItems() {
      if (activePackQty <= 1 || !bundleBuilder) return null;
      var items = [];
      for (var i = 0; i < activePackQty; i++) {
        var slot = bundleSlots[i];
        var variant = slot && variantForBundleSlot(slot);
        if (!variant || !variant.available) return null;
        items.push({ id: variant.id, quantity: 1 });
      }
      return items;
    }

    function updateBundleState() {
      var custom = activePackQty > 1 && !!bundleBuilder;
      root.classList.toggle('is-custom-bundle', custom);
      if (bundleBuilder) bundleBuilder.hidden = !custom;

      var allValid = true;
      var anyBackorder = false;
      bundleSlots.forEach(function (slot, index) {
        var visible = custom && index < activePackQty;
        slot.hidden = !visible;
        if (!visible) return;
        refreshBundleSlotOptions(slot);
        var variant = variantForBundleSlot(slot);
        var valid = !!(variant && variant.available);
        // one backordered pair delays the whole pack, so surface it here too
        if (variant && variant.backorder) anyBackorder = true;
        allValid = allValid && valid;
        slot.classList.toggle('is-unavailable', !valid);
        var status = slot.querySelector('[data-myc-bundle-slot-status]');
        if (status) {
          status.textContent = valid ? '' : bundleBuilder.getAttribute('data-error-label');
          status.hidden = valid;
        }
      });

      if (!custom) return;
      if (backorderNote) backorderNote.hidden = !anyBackorder;
      var label = activePackQty === 5 ? packFiveLabel : packThreeLabel;
      if (atcBtn) { atcBtn.disabled = !allValid; atcBtn.textContent = label; }
      if (barBtn) { barBtn.disabled = !allValid; barBtn.textContent = label; }
    }

    root.__mycBundleItems = selectedBundleItems;

    function restoreSinglePurchaseState() {
      var variant = currentVariant();
      var available = !!(variant && variant.available);
      var label = variant && variant.backorder ? atcBackorder : atcLabel;
      if (atcBtn) { atcBtn.disabled = !available; atcBtn.textContent = available ? label : atcSoldOut; }
      if (barBtn) { barBtn.disabled = !available; barBtn.textContent = available ? label : atcSoldOut; }
      if (backorderNote) backorderNote.hidden = !(variant && variant.backorder);
    }

    function setPack(qty) {
      activePackQty = qty;
      if (qtyInput) qtyInput.value = qty;
      if (qty > 1) seedBundleSlots();
      packBtns.forEach(function (button) {
        var active = parseInt(button.getAttribute('data-pack-qty'), 10) === qty;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      updateBundleState();
      if (qty <= 1) restoreSinglePurchaseState();
    }

    packBtns.forEach(function (p) {
      p.addEventListener('click', function () {
        var q = parseInt(p.getAttribute('data-pack-qty'), 10) || 1;
        setPack(q);
      });
    });

    if (bundleBuilder) {
      bundleBuilder.addEventListener('change', function (event) {
        if (event.target.closest('[data-myc-bundle-option]')) updateBundleState();
      });

      // Swatch click writes through to the slot's colour <select>, so the whole
      // existing availability/healing pass runs exactly as it does for a dropdown.
      bundleBuilder.addEventListener('click', function (event) {
        var swatch = event.target.closest('[data-myc-bundle-swatch]');
        if (!swatch || swatch.disabled) return;
        event.preventDefault();
        var slot = swatch.closest('[data-myc-bundle-slot]');
        if (!slot) return;
        var select = slot.querySelector('[data-myc-bundle-option][data-option-index="' + swatch.getAttribute('data-option-index') + '"]');
        if (!select) return;
        select.value = swatch.getAttribute('data-option-value');
        updateBundleState();
      });
    }

    // Manual quantities outside 1/3/5 use the currently selected single variant.
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var q = parseInt(qtyInput.value, 10) || 1;
        var hasPack = packBtns.some(function (button) { return parseInt(button.getAttribute('data-pack-qty'), 10) === q; });
        if (hasPack) setPack(q);
        else {
          activePackQty = 1;
          packBtns.forEach(function (button) { button.classList.remove('is-active'); button.setAttribute('aria-pressed', 'false'); });
          updateBundleState();
          restoreSinglePurchaseState();
        }
      });
    }

    /* ---------------- sticky mobile add-to-cart bar ---------------- */
    var bar = root.querySelector('[data-myc-atc-bar]');
    if (bar && atcBtn && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          // show the bar once the real buy button has scrolled out of view upward
          var below = entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', !entry.isIntersecting && below);
          bar.setAttribute('aria-hidden', (!entry.isIntersecting && below) ? 'false' : 'true');
        });
      }, { threshold: 0 });
      io.observe(atcBtn);
    }
    if (barBtn && form) {
      barBtn.addEventListener('click', function () {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }

    /* ---------------- variant state ---------------- */
    function update(mediaBehavior) {
      var variant = currentVariant();
      if (colorNameEl) {
        var colorIdx = colorNameEl.getAttribute('data-option-index');
        if (selected[colorIdx]) colorNameEl.textContent = selected[colorIdx];
      }
      if (!variant) {
        if (activePackQty > 1) { updateBundleState(); return; }
        if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = atcSoldOut; }
        if (barBtn) { barBtn.disabled = true; barBtn.textContent = atcSoldOut; }
        if (backorderNote) backorderNote.hidden = true;
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price);
      if (barPriceEl) barPriceEl.textContent = money(variant.price);
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = money(variant.compare_at_price);
          comparePriceEl.hidden = false;
        } else {
          comparePriceEl.hidden = true;
        }
      }
      // Buyable at zero stock ("continue selling") — still add to cart, but say so
      // and set the delivery expectation before they commit.
      var buyLabel = variant.backorder ? atcBackorder : atcLabel;
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        atcBtn.textContent = variant.available ? buyLabel : atcSoldOut;
      }
      if (barBtn) {
        barBtn.disabled = !variant.available;
        barBtn.textContent = variant.available ? buyLabel : atcSoldOut;
      }
      if (backorderNote) backorderNote.hidden = !variant.backorder;
      switchMedia(variant, mediaBehavior);

      // mark option availability against the current selection of other options
      root.querySelectorAll('[data-myc-option]').forEach(function (btn) {
        var oi = btn.getAttribute('data-option-index');
        var ov = btn.getAttribute('data-option-value');
        var hypothetical = Object.assign({}, selected);
        hypothetical[oi] = ov;
        var match = product.variants.find(function (v) {
          return v.options.every(function (opt, i) { return hypothetical[String(i)] === opt; });
        });
        btn.classList.toggle('is-unavailable', !(match && match.available));
      });

      if (window.history && window.history.replaceState && root.dataset.updateUrl === 'true') {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      updateBundleState();
    }

    // option button clicks
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-myc-option]');
      if (!btn) return;
      e.preventDefault();
      var oi = btn.getAttribute('data-option-index');
      selected[oi] = btn.getAttribute('data-option-value');
      root.querySelectorAll('[data-myc-option][data-option-index="' + oi + '"]').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
      });
      update('smooth');
    });

    update('auto');
  }

  function initAll() {
    document.querySelectorAll('[data-myc-product]').forEach(initProduct);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
